import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { DbFile, FileService } from '../../shared/public-api';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'admin-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss'],
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    ToastModule,
    TableModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    FileUploadModule,
  ],
  providers: [ConfirmationService, MessageService],
})
export class FileComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('dt') dataTable: Table;
  files: Observable<DbFile[]>;
  fileModels: DbFile[] = [];
  loading = false;
  createFile = false;
  baseUrl: string;

  constructor(
    private readonly fileService: FileService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.baseUrl = environment.baseUrl;
  }

  ngOnInit(): void {
    this.files = this.fileService.getFiles();
  }

  saveFiles(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.loading = true;
    const subscriptions = [];
    this.fileModels.forEach((file) => {
      subscriptions.push(this.fileService.createFile(file));
    });

    forkJoin(subscriptions)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.createFile = false;
          this.fileInput.nativeElement.value = '';
        }),
        catchError((err) =>
          of(
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err,
            })
          )
        )
      )
      .subscribe(() => {
        this.files = this.fileService.getFiles();
        this.messageService.add({
          severity: 'success',
          summary: 'Ok',
          detail: 'Fichier(s) créés!',
        });
      });
  }

  fileChanged(event: FileSelectEvent): void {
    const files = event.files;
    if (!files || files?.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileModel = new DbFile();
      fileModel.name = file.name;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        fileModel.uploadSrc = reader.result as string;
      };
      this.fileModels.push(fileModel);
    }
  }

  deleteFileConfirm(file: DbFile): void {
    this.confirmationService.confirm({
      message: 'Etes-vous sûr de supprimer ce fichier?',
      header: 'Supprimer un fichier',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteFile(file),
    });
  }

  deleteFile(file: DbFile): void {
    this.loading = true;
    this.fileService
      .deleteFile(file.id)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        catchError((err) =>
          of(
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err,
            })
          )
        )
      )
      .subscribe((res) => {
        this.files = this.fileService.getFiles();
        this.messageService.add({
          severity: 'success',
          summary: 'Ok',
          detail: 'Fichier supprimé!',
        });
      });
  }

  searchFile(target: EventTarget): void {
    const value = (target as HTMLInputElement).value;
    this.dataTable.filterGlobal(value, 'contains');
  }
}
